import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Archive, Download, Loader2, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });
const STATUS_CLS = { paid_full: 'bg-emerald-50 text-emerald-700', deposit_paid: 'bg-amber-50 text-amber-700', pending: 'bg-orange-50 text-orange-700', cancelled: 'bg-gray-100 text-gray-600', waitlist: 'bg-violet-50 text-violet-700' };

const ArchivePage = () => {
  const [archives, setArchives] = useState(null);
  const [archive, setArchive] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reserve/admin/season`, { headers: getAuthHeaders() })
      .then(async (r) => {
        setArchives(r.data.archives);
        if (r.data.archives[0]) {
          const full = await axios.get(`${BACKEND_URL}/api/reserve/admin/archives/${r.data.archives[0].id}`, { headers: getAuthHeaders() });
          setArchive(full.data);
        }
      }).catch(() => toast.error('Failed to load archives'));
  }, []);

  const exportCsv = async () => {
    const res = await axios.get(`${BACKEND_URL}/api/reserve/admin/archives/${archive.id}/export.csv`, { headers: getAuthHeaders(), responseType: 'blob' });
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(res.data), download: `roster-${archive.season}.csv` });
    a.click(); URL.revokeObjectURL(a.href);
  };

  if (!archives) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-wine" /></div>;

  if (archives.length === 0) return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="archive-page">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Season Archive</h1>
      <Card className="border-0 shadow-sm"><CardContent className="py-16 text-center text-gray-500" data-testid="archive-empty">
        <Archive className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        Nothing archived yet. When you start the 2027 season from <strong>2027 Reservations</strong>, the full 2026 roster lands here.
      </CardContent></Card>
    </div>
  );

  const rows = (archive?.attendees || [])
    .filter(a => (status === 'all' || a.ticket_status === status) && (!q.trim() || `${a.first_name} ${a.last_name} ${a.email}`.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '') || (a.first_name || '').localeCompare(b.first_name || ''));
  const s = archive?.summary || {};

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="archive-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{archive?.season} Archive</h1>
          <p className="text-gray-600 mt-1">Read-only snapshot taken {archive && new Date(archive.archived_at).toLocaleString()} by {archive?.archived_by}</p>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={!archive} data-testid="export-archive-btn"><Download className="w-4 h-4 mr-2" />Export roster CSV</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[['Attended', s.active], ['Cancelled', s.cancelled], ['Waitlist', s.waitlist], ['Rooms', s.rooms], ['Cash collected', `$${(s.cash_collected || 0).toLocaleString()}`]].map(([l, v]) => (
          <Card key={l} className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-gray-400 uppercase tracking-wide">{l}</p><p className="text-2xl font-bold text-gray-900 mt-1">{v ?? 0}</p></CardContent></Card>
        ))}
      </div>

      <Card className="border-0 shadow-sm"><CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 flex-wrap">
            {['all', 'paid_full', 'deposit_paid', 'pending', 'cancelled', 'waitlist'].map(k => (
              <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1 text-xs font-medium rounded-md ${status === k ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-800'}`} data-testid={`archive-filter-${k}`}>{k.replace('_', ' ')}</button>
            ))}
          </div>
          <div className="relative sm:ml-auto sm:w-72"><Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" /><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email" className="pl-9" data-testid="archive-search" /></div>
        </div>
        <div className="overflow-x-auto max-h-[36rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white"><tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b">
              <th className="py-2 pr-4 font-semibold">Name</th><th className="py-2 pr-4 font-semibold">Email</th><th className="py-2 pr-4 font-semibold">Status</th><th className="py-2 pr-4 font-semibold">Paid</th><th className="py-2 pr-4 font-semibold">Room</th><th className="py-2 pr-4 font-semibold">Shirt</th>
            </tr></thead>
            <tbody>
              {rows.map(a => (
                <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`archive-row-${a.id}`}>
                  <td className="py-2 pr-4 font-medium text-gray-800">{a.last_name}, {a.first_name}</td>
                  <td className="py-2 pr-4 text-gray-500 text-xs">{a.email}</td>
                  <td className="py-2 pr-4"><Badge className={`${STATUS_CLS[a.ticket_status] || 'bg-gray-100 text-gray-600'} border-0 text-xs`}>{a.ticket_status?.replace('_', ' ')}</Badge></td>
                  <td className="py-2 pr-4 text-gray-700">{a.amount_paid != null ? `$${a.amount_paid}` : '—'}{a.scholarship_amount != null && <span className="text-pink-600 text-xs ml-1">+schol.</span>}{a.refund_status === 'paid' && <span className="text-gray-400 text-xs ml-1">refunded</span>}</td>
                  <td className="py-2 pr-4 text-gray-600">{a._room_name || '—'}</td>
                  <td className="py-2 pr-4 text-gray-600">{a.tshirt_size || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-400 mt-3">{rows.length} of {archive?.attendees?.length || 0} shown</p>
        </div>
      </CardContent></Card>
    </div>
  );
};

export default ArchivePage;
